<template>
    <vx-box class="p-4">
        <div class="flex justify-between items-start mb-4">
            <div>
                <meta-badge
                    v-if="senderBadge"
                    :icon="senderBadge.icon"
                    :color="senderBadge.color"
                    :data-test="`${props.campaign.sender.type}-badge`"
                >
                    {{ senderBadge.label }}
                </meta-badge>

                <div class="text-base text-slate-800">{{ props.campaign.name }}</div>
            </div>
            <div
                v-if="onCorporate() || (props.orgUnitId && props.campaign.sender.type !== 'corporate')"
                class="hidden sm:block"
            >
                <vx-dropdown placement="right">
                    <vx-button size="sm" type="button" color="muted" outline class="w-[37px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item
                            v-if="props.campaign.status === 'scheduled'"
                            data-test="set-to-draft"
                            @click="setToDraft()"
                        >
                            Set to Draft
                            <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item data-test="delete" @click="deleteCampaign()">
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>
            </div>
        </div>

        <div class="flex flex-col gap-4 sm:flex-row sm:justify-between">
            <div class="flex flex-col gap-1">
                <div class="flex items-center">
                    <div class="w-[70px] text-xs text-slate-500">Status:</div>
                    <div>
                        <campaign-badge icon :status="props.campaign.status" />
                    </div>
                </div>
                <div class="flex items-center">
                    <div class="w-[70px] text-xs text-slate-500">Scheduled:</div>
                    <div class="text-sm text-slate-800">
                        {{ toShortDateTimeRelativeDays(props.campaign.live_at, props.timezone) }}
                    </div>
                </div>
                <div class="flex items-center">
                    <div class="w-[70px] text-xs text-slate-500">Messages:</div>
                    <div class="text-sm text-slate-800">
                        <vx-popover
                            class="cursor-pointer"
                            arrow
                            placement="top"
                            @click="copyText(props.campaign.audience_size)"
                            hover
                        >
                            {{ vxFormatCount(props.campaign.audience_size) }}
                            <template v-slot:content>
                                <div class="text-center" @click="copyText(props.campaign.audience_size)">
                                    {{ Intl.NumberFormat('en-US').format(props.campaign.audience_size) }}
                                    <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                                </div>
                            </template>
                        </vx-popover>
                    </div>
                </div>
                <div class="flex items-center">
                    <div class="w-[70px] text-xs text-slate-500">Estimated Segments:</div>
                    <div class="text-sm text-slate-800">
                        {{ props.campaign.sms_segments }}
                    </div>
                </div>
            </div>

            <div class="sm:max-w-1/2">
                <message-cloud fullWidth :message="props.campaign.main_message" :showDetails="false" />
            </div>
        </div>

        <div
            v-if="onCorporate() || (props.orgUnitId && props.campaign.sender.type !== 'corporate')"
            class="w-full mt-4 block sm:hidden"
        >
            <vx-dropdown placement="right">
                <vx-button size="sm" type="button" color="muted-light" class="w-full sm:w-[37px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item
                        v-if="props.campaign.status === 'scheduled'"
                        data-test="set-to-draft"
                        @click="setToDraft()"
                    >
                        Set to Draft
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="deleteCampaign()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>
        </div>
    </vx-box>
</template>

<script setup lang="ts">
import { useCopyText } from '~/composables/useCopyText';
import {
    VxButton,
    VxBox,
    VxDropdown,
    VxDropdownItem,
    vxFormatCount,
    VxPopover,
    VxSwal,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, inject } from 'vue';
import type { BlastForecastCampaignsResponse } from '~/services/blastForecast/blastForecastClient.types';
import { toShortDateTimeRelativeDays } from '~/utils/date';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import {
    faEllipsis,
    faTrash,
    faPause,
    faCrown,
    faBuildingFlag,
    faGrid2Plus,
    type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { campaignsClient } from '~/services';
import orgUnitGroupsClient from '~/services/orgUnitGroupsClient';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import CampaignBadge from '~/components/campaigns/campaign-card/CampaignBadge.vue';
import type { Features } from '~/provides/features.types';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import type { CampaignStatus } from '~/constants/campaign';
import MetaBadge from '~/components/general/MetaBadge.vue';
import type { MetaBadgeColor } from '~/components/general/MetaBadge.types';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const emit = defineEmits<{
    (e: 'deleted', campaignId: number): void;
    (e: 'statusChanged', campaignId: number, newStatus: CampaignStatus): void;
}>();

const props = defineProps<{
    campaign: BlastForecastCampaignsResponse['items'][0];
    timezone?: string;
    orgUnitId?: number;
}>();

const senderBadge = computed(
    (): {
        icon: IconDefinition;
        color: MetaBadgeColor;
        label: string;
    } | void => {
        if (!FEATURES.contact_groups) {
            return;
        }

        const senderType = props.campaign.sender.type;

        switch (senderType) {
            case 'corporate':
                return {
                    icon: faCrown,
                    color: 'amber',
                    label: 'Corporate',
                };
            case 'org_unit':
                return {
                    icon: faBuildingFlag,
                    color: 'violet',
                    label: props.campaign.sender.name,
                };
            case 'group':
                return {
                    icon: faGrid2Plus,
                    color: 'slate',
                    label: props.campaign.sender.name!,
                };
        }
        return undefined;
    }
);

const { copyText } = useCopyText();

const toasted = useToasted();

const { onCorporate } = useCurrentGroup();

const validateCampaignAccess = async (): Promise<boolean> => {
    if (!props.orgUnitId) {
        return true;
    }

    if (props.campaign.sender.type === 'org_unit' && props.campaign.sender.id !== props.orgUnitId) {
        toasted.error('This campaign belongs to another OU');
        return false;
    }

    if (props.campaign.sender.type === 'group') {
        try {
            await orgUnitGroupsClient.showOrgUnitGroup(teamId, props.orgUnitId, props.campaign.sender.id);
            return true;
        } catch (e) {
            catchRequestError(e);
            toasted.error('You do not have permission to change this campaign');
            return false;
        }
    }

    return true;
};

const setToDraft = () => {
    VxSwal.confirmation(
        'Set Campaign to Draft',
        'Are you sure you want to set this campaign to draft?',
        'question',
        async () => {
            if (!(await validateCampaignAccess())) {
                return;
            }

            let changeStatusRequest;

            if (props.campaign.sender.type === 'corporate') {
                changeStatusRequest = campaignsClient.changeStatus(teamId, props.campaign.id, 'draft');
            } else if (props.campaign.sender.type === 'org_unit') {
                changeStatusRequest = campaignsClient.orgUnitCampaignChangeStatus(
                    teamId,
                    props.campaign.sender.id,
                    props.campaign.id,
                    'draft'
                );
            } else {
                changeStatusRequest = campaignsClient.groupCampaignChangeStatus(
                    teamId,
                    props.campaign.sender.id,
                    props.campaign.id,
                    'draft'
                );
            }

            try {
                await changeStatusRequest;
                emit('statusChanged', props.campaign.id, 'draft');
                toasted.success('Campaign set to draft');
            } catch (e) {
                throwSwalRequestError(e);
            }
        }
    );
};

const deleteCampaign = () => {
    VxSwal.confirmation(
        'Delete Campaign',
        'Are you sure? This campaign and its messages will be deleted. There is no undo.',
        'danger',
        async () => {
            if (!(await validateCampaignAccess())) {
                return;
            }

            let deleteRequest;

            if (props.campaign.sender.type === 'corporate') {
                deleteRequest = campaignsClient.campaignDelete(teamId, props.campaign.id);
            } else if (props.campaign.sender.type === 'org_unit') {
                deleteRequest = campaignsClient.orgUnitCampaignDelete(
                    teamId,
                    props.campaign.sender.id,
                    props.campaign.id
                );
            } else {
                deleteRequest = campaignsClient.groupCampaignDelete(
                    teamId,
                    props.campaign.sender.id,
                    props.campaign.id
                );
            }

            try {
                await deleteRequest;
                emit('deleted', props.campaign.id);
                toasted.success('Campaign deleted');
            } catch (e) {
                throwSwalRequestError(e);
            }
        }
    );
};
</script>
